import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Form,
  Select,
  Button,
  Card,
  Typography,
  Segmented,
  Row,
  Col,
  message,
  Input,
  Modal,
} from 'antd';
import { Button as MobileButton, Selector, Toast, Input as MobileInput, Dialog } from 'antd-mobile';
import {
  ColumnHeightOutlined,
  DashboardOutlined,
  SendOutlined,
  UserOutlined,
  TeamOutlined,
  EditOutlined,
} from '@ant-design/icons';
import WorkwearPreview from '../../components/WorkwearPreview/WorkwearPreview';
import StepProgress from '../../components/StepProgress/StepProgress';
import { checkRecordExists, insertRecord } from '../../db/database';
import { getSizeInfo, getSizeSelectOptions } from '../../data/sizeReference';
import { useUser } from '../../context/UserContext';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { OVERWRITE_CONFIRM } from '../../utils/applyConfirm';
import styles from './Apply.module.css';

const { Title } = Typography;

const REMARK_OTHER = '__other__';

const REMARK_PRESET_OPTIONS = [
  { label: '肚子圆润一点', value: '肚子圆润' },
  { label: '偏好宽松', value: '偏好宽松' },
  { label: '偏好修身', value: '偏好修身' },
  { label: '其他', value: REMARK_OTHER },
];

const REMARK_OPTIONS = [
  { label: '无特殊需求', value: '' },
  ...REMARK_PRESET_OPTIONS,
];

const COLOR_ALLOTMENT = '黑色*1/白色*1';

export default function ApplyPage() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { profile, hasProfile } = useUser();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [gender, setGender] = useState('male');
  const [size, setSize] = useState('L');
  const [remarkPreset, setRemarkPreset] = useState('');
  const [remarkCustom, setRemarkCustom] = useState('');

  const sizeInfo = getSizeInfo(size);
  const sizeOptions = getSizeSelectOptions();

  const resolveRemark = () => {
    if (remarkPreset === REMARK_OTHER) return remarkCustom.trim();
    return remarkPreset;
  };

  const handleRemarkPresetChange = (val) => {
    setRemarkPreset(val || '');
    if (val !== REMARK_OTHER) setRemarkCustom('');
  };

  useEffect(() => {
    if (!hasProfile) navigate('/', { replace: true });
  }, [hasProfile, navigate]);

  const submitRecord = async (values, overwrite = false) => {
    setSubmitting(true);
    try {
      await insertRecord({
        name: profile.name,
        department: profile.department,
        gender: gender === 'male' ? '小哥哥' : '小姐姐',
        size: values?.size || size,
        color: COLOR_ALLOTMENT,
        remark: resolveRemark(),
        overwrite,
      });
      if (isMobile) {
        Toast.show({ icon: 'success', content: '福利工装申领成功' });
      } else {
        message.success('福利工装申领成功');
      }
      navigate('/success', { state: { name: profile.name, size: values?.size || size } });
    } catch (err) {
      console.error(err);
      const msg = err?.message || '提交失败，请重试';
      if (isMobile) {
        Toast.show({ icon: 'fail', content: msg });
      } else {
        message.error(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const confirmOverwrite = () =>
    new Promise((resolve) => {
      if (isMobile) {
        Dialog.confirm({
          title: OVERWRITE_CONFIRM.title,
          content: OVERWRITE_CONFIRM.content,
          confirmText: OVERWRITE_CONFIRM.okText,
          cancelText: OVERWRITE_CONFIRM.cancelText,
          onConfirm: () => resolve(true),
          onCancel: () => resolve(false),
        });
      } else {
        Modal.confirm({
          title: OVERWRITE_CONFIRM.title,
          content: OVERWRITE_CONFIRM.content,
          okText: OVERWRITE_CONFIRM.okText,
          cancelText: OVERWRITE_CONFIRM.cancelText,
          centered: true,
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
      }
    });

  const handleSubmit = async (values) => {
    if (submitting) return;

    try {
      const { exists } = await checkRecordExists({
        name: profile.name,
        department: profile.department,
      });

      if (exists) {
        const confirmed = await confirmOverwrite();
        if (!confirmed) return;
        await submitRecord(values, true);
        return;
      }

      await submitRecord(values, false);
    } catch (err) {
      console.error(err);
      const msg = err?.message || '提交失败，请重试';
      if (isMobile) {
        Toast.show({ icon: 'fail', content: msg });
      } else {
        message.error(msg);
      }
    }
  };

  const userInfoBar = (
    <div className={styles.userBar}>
      <span className={styles.userBarItem}><UserOutlined /> {profile.name}</span>
      <span className={styles.userBarItem}><TeamOutlined /> {profile.department}</span>
      <button type="button" className={styles.editLink} onClick={() => navigate('/')}>
        <EditOutlined /> 修改
      </button>
    </div>
  );

  const sizeInfoBlock = (
    <>
      <div className={styles.sizeInfoTitle}>{size} · {sizeInfo.friendlyName}</div>
      <p className={styles.refLine}>
        <span><ColumnHeightOutlined /> {sizeInfo.height}</span>
        <span><DashboardOutlined /> {sizeInfo.weight}</span>
      </p>
      <p className={styles.taglinePc}>{sizeInfo.tagline}</p>
    </>
  );

  if (!hasProfile) return null;

  if (isMobile) {
    return (
      <section className={`card ${styles.applyPage}`}>
        <StepProgress current={2} />
        <div className={styles.pageHead}>
          <Title level={4} className={styles.pageTitle}>选择福利工装尺码</Title>
          <p className={styles.pageDesc}>选择尺码与款式，乐福集采将统一安排发放</p>
        </div>

        {userInfoBar}

        <div className={styles.previewSection}>
          <div className={styles.previewStage}>
            <WorkwearPreview gender={gender} size={size} showBadge={false} />
          </div>
          <div className={styles.sizeHintMobile}>
            <span className={styles.sizeName}>{sizeInfo.friendlyName}</span>
            <span className={styles.refText}>参考 {sizeInfo.height} · {sizeInfo.weight}</span>
            <p className={styles.tagline}>{sizeInfo.tagline}</p>
          </div>
        </div>

        <div className={styles.formFieldsMobile}>
          <div className={styles.fieldGroup}>
            <label>性别</label>
            <Selector
              className={styles.selectorGrid2}
              columns={2}
              options={[
                { label: '小哥哥', value: 'male' },
                { label: '小姐姐', value: 'female' },
              ]}
              value={[gender]}
              onChange={(v) => v.length && setGender(v[0])}
            />
          </div>

          <div className={styles.fieldGroup}>
            <label>工装尺码</label>
            <Selector
              className={styles.selectorGrid4}
              columns={4}
              options={sizeOptions.map((o) => ({ label: o.value, value: o.value }))}
              value={[size]}
              onChange={(v) => v.length && setSize(v[0])}
            />
          </div>

          <div className={styles.fieldGroup}>
            <label>颜色</label>
            <div className={styles.disabledField}>{COLOR_ALLOTMENT}</div>
          </div>

          <div className={styles.fieldGroup}>
            <label>备注（选填）</label>
            <Selector
              className={styles.selectorGrid2}
              columns={2}
              options={REMARK_PRESET_OPTIONS}
              value={remarkPreset ? [remarkPreset] : []}
              onChange={(v) => handleRemarkPresetChange(v[0] || '')}
            />
            {remarkPreset === REMARK_OTHER && (
              <MobileInput
                className={styles.remarkCustomInput}
                placeholder="请填写备注内容"
                value={remarkCustom}
                onChange={setRemarkCustom}
                maxLength={100}
                clearable
              />
            )}
          </div>

          <div className={styles.submitWrap}>
            <MobileButton
              block
              color="primary"
              size="large"
              loading={submitting}
              onClick={() => handleSubmit({})}
              className={styles.submitBtnMobile}
            >
              提交福利申领
            </MobileButton>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={`card ${styles.applyPage}`}>
      <StepProgress current={2} />
      <div className={styles.pageHead}>
        <Title level={3} className={styles.pageTitle}>选择福利工装尺码</Title>
        <p className={styles.pageDesc}>选择尺码与款式，乐福集采将统一安排发放</p>
      </div>

      {userInfoBar}

      <Row gutter={[12, 12]} className={styles.mainRow}>
        <Col xs={24} md={10}>
          <Card className={styles.previewCard} bordered={false}>
            <div className={styles.previewInner}>
              <WorkwearPreview gender={gender} size={size} showBadge={false} />
            </div>
            <div className={styles.sizeInfo}>{sizeInfoBlock}</div>
          </Card>
        </Col>

        <Col xs={24} md={14}>
          <Card className={styles.formCard} bordered={false}>
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              initialValues={{ size: 'L' }}
              requiredMark={false}
            >
              <Form.Item label="性别">
                <Segmented
                  block
                  size="large"
                  value={gender}
                  onChange={setGender}
                  options={[
                    { label: '小哥哥', value: 'male' },
                    { label: '小姐姐', value: 'female' },
                  ]}
                />
              </Form.Item>

              <Form.Item
                label="工装尺码"
                name="size"
                rules={[{ required: true, message: '请选择尺码' }]}
              >
                <Select
                  size="large"
                  options={sizeOptions}
                  onChange={(val) => setSize(val)}
                  placeholder="请选择尺码"
                />
              </Form.Item>

              <Form.Item label="颜色" className={styles.readonlyItem}>
                <div className={styles.disabledField}>{COLOR_ALLOTMENT}</div>
              </Form.Item>

              <Form.Item label="备注">
                <Select
                  size="large"
                  allowClear
                  placeholder="选填"
                  value={remarkPreset || undefined}
                  options={REMARK_OPTIONS}
                  onChange={handleRemarkPresetChange}
                />
                {remarkPreset === REMARK_OTHER && (
                  <Input
                    size="large"
                    className={styles.remarkCustomInput}
                    placeholder="请填写备注内容"
                    value={remarkCustom}
                    onChange={(e) => setRemarkCustom(e.target.value)}
                    maxLength={100}
                    allowClear
                  />
                )}
              </Form.Item>

              <Form.Item style={{ marginBottom: 0 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  loading={submitting}
                  block
                  icon={<SendOutlined />}
                  className={styles.submitBtn}
                >
                  提交福利申领
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>
      </Row>
    </section>
  );
}
